import { explodingObject } from '@util/contextHelpers';
import React, { useContext, useMemo } from 'react';

export interface GlobalLoadingContextType {
    isLoading: boolean;
    startGlobalLoading(): void;
    stopGlobalLoading(): void;
    globalLoadingWrapped: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
}

const GlobalLoadingContext = React.createContext<GlobalLoadingContextType>(
    explodingObject('GlobalLoadingContext used without being provided')
);

export function useGlobalLoadingContext() {
    return useContext(GlobalLoadingContext);
}

export function GlobalLoadingContextProvider({ children }: React.PropsWithChildren) {
    const [loadingCounter, setLoadingCounter] = React.useState(0);

    const value = useMemo(() => {
        return {
            // Avoid using these, wrap promises instead
            startGlobalLoading: () => setLoadingCounter((current) => current + 1),
            stopGlobalLoading: () => setLoadingCounter((current) => Math.max(current - 1, 0)),

            // Wrap a promise
            globalLoadingWrapped: async <T,>(fn: () => Promise<T>) => {
                let result: T | undefined;
                setLoadingCounter((current) => current + 1);
                try {
                    result = await fn();
                } finally {
                    setLoadingCounter((current) => Math.max(current - 1, 0));
                }
                return result;
            },
            isLoading: loadingCounter > 0,
        };
    }, [loadingCounter]);

    return <GlobalLoadingContext.Provider value={value}>{children}</GlobalLoadingContext.Provider>;
}
